import { CountryCodes, useCountryCodesValidation } from '@e3dc-react/shell/Hooks/useCountryOptions'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { AppLang } from '@e3dc-react/shell/State/App/AppModel'
import { UserLevel } from '@e3dc-react/shell/Types'
import { INSTALLER_PARTNER } from 'src/Configuration'
import { stringSchema } from 'src/Configuration/validation/rules'
import { FormInitializerReturnType } from 'src/Configuration/validation'
import { useCompanyUsers } from 'src/Libs/company-settings'
import { getInitialLanguageByCountry } from 'src/Libs/languages/functions/default'
import { useCompanies, usePresetUserCompany, UserForm } from 'src/Libs/service-dashboard'
import { compareAddresses } from 'src/Libs/service-dashboard/functions'
import { User } from 'src/State/User/UserModel'
import { useMemo } from 'react'
import { boolean, number, NumberSchema, object, string, StringSchema } from 'yup'
import { buildInstallerUsernameRegex } from './use-user-view-form-initializer.utils'

interface ReturnType extends FormInitializerReturnType<Omit<UserForm, 'companyName'>, Omit<UserForm, 'companyName'>> {
  isUsernameValid: (
    username: string,
    userLevel: string,
    companyId: number | undefined,
    firstName: string,
    lastName: string
  ) => Promise<{ isUsernameValidCompanyEmail: boolean; isUsernameValidFirstAndLastName: boolean }>
}

const MAX_USERNAME_LENGTH = 255
const MAX_NAME_LENGTH = 50
const MAX_ZIP_LENGTH = 10
const IPIN_LENGTH = 6

/**
 *  Initializes the form for editing or creating a user
 * @param user User
 * @param isEditing boolean
 * @returns initialValues and validationSchema for user form and username validation function
 */
export const useUserViewFormInitializer = (user?: User, isEditing?: boolean): ReturnType => {
  const { t } = useCustomTranslation()
  const stringSchemaWithTranslation = stringSchema(t)

  /** MARK: Initial Values */
  const [validateCountryCode] = useCountryCodesValidation()
  const userContactCountry = user?.contactData?.Land ?? ''
  const initialCountryValue = isEditing ? '' : CountryCodes.GERMANY
  const country = (validateCountryCode(userContactCountry) ? userContactCountry : initialCountryValue) as CountryCodes

  const { getCompanyLocation } = useCompanies()

  const { users: usersByCompany } = useCompanyUsers(user?.company?.id as number, user?.id)
  const { initialCompanyId = user?.company?.id, initialUseCompanyAddress, initialUserLevel } = usePresetUserCompany()

  const permissionsOfTheCurrentUser = usersByCompany?.find(elem => elem.ID === user?.id)?.permissions
  const initialValues = useMemo<UserForm>(
    () => ({
      username: user?.userData.username ?? '',
      eMail: user?.contactData?.Email ?? '',
      userLevel: user?.userData?.level ?? (initialUserLevel as UserLevel),
      phoneNumber: user?.contactData?.Telefon ?? '',
      firstName: user?.userData?.Vorname ?? '',
      lastName: user?.userData?.Name ?? '',
      address: user?.contactData?.PlaceOR ?? '',
      zipCode: user?.contactData?.PLZ ?? '',
      city: user?.contactData?.Address ?? '',
      country,
      company: initialCompanyId,
      ipin: user?.userData?.ipin ?? '',
      eMailNotification: user?.userData?.mailNotification ?? false,
      infinityCertified: user?.userData?.infinityCertified ?? false,
      useShop: permissionsOfTheCurrentUser?.useShop ?? false,
      couponPermission: user?.userData?.couponPermission ?? false,
      language: user?.userData?.language ?? getInitialLanguageByCountry(country),
      useCompanyAddress: isEditing ? compareAddresses(user?.contactData, user?.company?.contactData) : initialUseCompanyAddress,
      qntmCertified: user?.userData?.qntmCertified ?? false,
    }),
    [country, initialCompanyId, initialUseCompanyAddress, initialUserLevel, isEditing, permissionsOfTheCurrentUser?.useShop, user]
  )

  /** MARK: Main Schema Validation */
  const validationSchema = object().shape({
    username: stringSchemaWithTranslation({ maxLength: MAX_USERNAME_LENGTH })
      .required()
      .matches(/^\S*$/, t('validations.form.username.noWhitespace')),
    firstName: stringSchemaWithTranslation({ maxLength: MAX_NAME_LENGTH }).required(),
    lastName: stringSchemaWithTranslation({ maxLength: MAX_NAME_LENGTH }).required(),
    eMail: string().email().required(t('validation.error-message.required')),
    userLevel: string<UserLevel>().oneOf(['USER', 'INSTALLER', 'PARTNER', 'E3DC_SERVICE', 'EXT_SERVICE_PLUS', 'EXT_SERVICE']).required(),
    address: string(),
    zipCode: stringSchemaWithTranslation({ maxLength: MAX_ZIP_LENGTH }),
    city: string(),
    country: string(),
    phoneNumber: string(),
    ipin: string().when('userLevel', {
      is: 'INSTALLER',
      then: (schema: StringSchema) =>
        schema.required().matches(/^\d+$/, t('validations.form.ipin.invalidFormat')).length(IPIN_LENGTH, t('validations.form.ipin.invalidFormat')),
    }),
    language: string<AppLang>(),
    company: number().when('userLevel', {
      is: (value: string) => INSTALLER_PARTNER.includes(value as UserLevel),
      then: (schema: NumberSchema) => schema.required(),
    }),
    infinityCertified: boolean(),
    eMailNotification: boolean().required(),
    longitude: number(),
    latitude: number(),
    useShop: boolean().required(),
    couponPermission: boolean().required(),
    useCompanyAddress: boolean(),
    qntmCertified: boolean(),
  })

  /** MARK: Additional Username Validation */
  /** These validation is only used to inform the user about the suggested email-type - they can still decide to save it as is */

  /** If the userLevel is installer/partner, username should be an email (not the main-company-email) */
  const userNameCompanyEmailSchema = object().shape({
    username: string()
      .when('userLevel', {
        is: 'E3DC_SERVICE',
        then: (schema: StringSchema) => schema.email(),
      })
      .when('userLevel', {
        is: (value: string) => INSTALLER_PARTNER.includes(value as UserLevel),
        then: (schema: StringSchema) =>
          schema.email().test('isValidCompanyEmail', async function (value, context) {
            if (!value) return false

            const userForm = context.parent as UserForm
            const companyId = userForm.company

            if (companyId) {
              const companyLocation = await getCompanyLocation(companyId)
              /** The input email should not be the same as of the company */
              return companyLocation?.Email !== value
            }
            return true
          }),
      }),
  })

  /** Is the userLevel is installer, username should consist of the first- & lastname */
  const userNameRegexSchema = object().shape({
    username: string()
      .when('userLevel', {
        is: 'E3DC_SERVICE',
        then: (schema: StringSchema) => schema.email(),
      })
      .when('userLevel', {
        is: 'INSTALLER',
        then: (schema: StringSchema) =>
          schema.test('isUsernameValidFirstAndLastName', async (value, context) => {
            if (!value) return false
            const regex = buildInstallerUsernameRegex(context.parent.firstName, context.parent.lastName)
            return regex.test(value)
          }),
      }),
  })

  const isUsernameValid = async (
    username: string,
    userLevel: string,
    companyId: number | undefined,
    firstName: string,
    lastName: string
  ): Promise<{ isUsernameValidCompanyEmail: boolean; isUsernameValidFirstAndLastName: boolean }> => {
    const isUsernameValidCompanyEmail = await userNameCompanyEmailSchema.isValid({
      company: companyId,
      username,
      userLevel,
    })

    const isUsernameValidFirstAndLastName = await userNameRegexSchema.isValid({
      firstName,
      lastName,
      userLevel,
      username,
    })

    return { isUsernameValidCompanyEmail, isUsernameValidFirstAndLastName }
  }

  return { initialValues, validationSchema, isUsernameValid }
}
